import React, { useEffect } from "react";
import ContactSeller from "../sellers/ContactSeller";
import SimilarItemsByLocation from "./SimilarItemsByLocation";
import MediaGallery from "./MediaGallery";
import { LuClock, LuInstagram, LuMapPin } from "react-icons/lu";
import ListingsHelper from "@/utils/ListingsHelper";
import StatsHelper from "@/utils/StatsHelper";
import CommonUtils from "@/utils/CommonUtils";
import AppDialog from "../AppDialog";
import axios from "axios";

const ListingDetails = ({
  listing,
  fullLocation,
  similarItemsByLocation,
  locale,
  isOwner = false,
  ownerHasInstagram = false,
  isForPreview = false,
}) => {
  let labels = ListingsHelper.getLabelsAndValues(listing);
  let priceData = ListingsHelper.getPriceLabels(listing);
  let [showDialog, setShowDialog] = React.useState(false);
  let [isPosting, setIsPosting] = React.useState(false);
  let [isCheckingIfPostedOnInstagram, setIsCheckingIfPostedOnInstagram] =
    React.useState(false);
  let [dialogTitle, setDialogTitle] = React.useState("");
  let [dialogMessage, setDialogMessage] = React.useState("");
  let [dialogType, setDialogType] = React.useState<any>("error");
  let [isPostedOnInstagram, setIsPostedOnInstagram] = React.useState(null);

  let [instagramPostURL, setInstagramPostURL] = React.useState(null);

  const ls = locale === "sw" ? "" : `/${locale}`;
  let title = locale === "sw" ? listing.title : listing.title_en;
  let slug = ListingsHelper.createSlugFromTitle(title);

  let date = listing.createdAt;

  const locationLink =
    ListingsHelper.getLocationLinkForSimilarListings(listing);

  useEffect(() => {
    StatsHelper.registerListingView(listing.id);
  }, []);

  let additionalProperty = null;

  if (listing.number_of_bedrooms) {
    additionalProperty = [
      {
        "@type": "PropertyValue",
        name: "bedrooms",
        value: listing.number_of_bedrooms,
      },
    ];
  }

  const postToInstagram = async () => {
    if (!isOwner) return;
    setIsPosting(true);
    const url = "/api/agent-admin/post/post-to-instagram";
    const body = {
      uuid: listing.uuid,
    };

    try {
      const response = await axios.post(url, body);
      if (response.data.success) {
        setDialog("success", "Success", "Posted to Instagram successfully");
        setIsPostedOnInstagram(true);
        setInstagramPostURL(response.data.data.external_url);
      } else {
        setDialog(
          "error",
          "Error",
          response.data.error || "An unexpected error occurred"
        );
      }
    } catch (error) {
      setDialog("error", "Error", "An unexpected error occurred");
    }
    setIsPosting(false);
  };

  const getInstagramPostIdIfExists = async () => {
    setIsCheckingIfPostedOnInstagram(true);
    const url = "/api/agent-admin/post/is-posted-on-instagram";
    const body = {
      uuid: listing.uuid,
    };
    try {
      const response = await axios.post(url, body);
      if (response.data.success) {
        setIsPostedOnInstagram(true);
        setInstagramPostURL(response.data.data.external_url);
      } else {
        setIsPostedOnInstagram(false);
        setInstagramPostURL(null);
      }
    } catch (error) {
      console.log("getInstagramPostIdIfExists", error);
    }
    setIsCheckingIfPostedOnInstagram(false);
  };
  useEffect(() => {
    getInstagramPostIdIfExists();
  }, []);

  const setDialog = (type: string, title: string, message: string) => {
    setDialogType(type);
    setDialogTitle(title);
    setDialogMessage(message);
    setShowDialog(true);
  };

  let timeAgo = CommonUtils.timeAgo(date);
  return (
    <>
      {showDialog && (
        <AppDialog
          type={dialogType}
          title={dialogTitle}
          text={dialogMessage}
          onClose={() => {
            setShowDialog(false);
          }}
        />
      )}
      {isPosting && (
        <AppDialog
          type="loader"
          title="Posting to Instagram"
          text="Please wait..."
        />
      )}

      <div className="container mx-auto">
        <div className="md:flex block md:space-x-5">
          <div className="flex-1">
            <article className="space-y-8 ">
              <h1 className="text-2xl md:text-4xl font-medium">{title}</h1>
              {isOwner && (
                <div className="flex items-center text-xs space-x-2 justify-between">
                  <div className="bg-gray-100 uppercase text-gray-600 p-2 rounded-md">
                    {listing.publish_status}
                  </div>
                  <div className="flex space-x-2">
                    {ownerHasInstagram && !listing.raw_listing_data_id && (
                      <>
                        {isCheckingIfPostedOnInstagram ? (
                          <div className="bg-gray-100 flex items-center space-x-2 uppercase text-gray-600 p-2 rounded-md">
                            Loading...
                          </div>
                        ) : (
                          <>
                            {!isPostedOnInstagram ? (
                              <button
                                onClick={() => {
                                  postToInstagram();
                                }}
                                className="bg-blue-600 text-sm text-white p-2 rounded-md flex items-center space-x-2"
                              >
                                <LuInstagram className="icons" />
                                <span>Post on Instagram</span>
                              </button>
                            ) : (
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => {
                                    window.open(instagramPostURL, "_blank");
                                  }}
                                  className="bg-gray-100 flex items-center space-x-2 uppercase text-gray-600 p-2 rounded-md"
                                >
                                  <LuInstagram className="icons" />
                                  <span>View on Instagram</span>
                                </button>
                                <button
                                  onClick={() => {
                                    postToInstagram();
                                  }}
                                  className="bg-blue-600 text-sm text-white p-2 rounded-md flex items-center space-x-2"
                                >
                                  <LuInstagram className="icons" />
                                  <span>Re-Post</span>
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </>
                    )}

                    {listing.raw_listing_data_id && (
                      <div
                        title="This listing is imported from Instagram"
                        className="bg-gray-100 flex items-center space-x-2 uppercase text-gray-600 p-2 rounded-md"
                      >
                        <LuInstagram className="icons" />
                      </div>
                    )}
                  </div>
                </div>
              )}
              <MediaGallery mediaList={listing.media} />
              <div className="sm:flex  md:flex justify-between items-center">
                <div className="flex items-center text-slate-600 space-x-1">
                  <LuMapPin className=" icons" />
                  <a
                    className=""
                    href={`${ls}/${locationLink}`}
                    title={fullLocation.full_name}
                  >
                    {listing.location.full_name}
                  </a>
                  <div className="flex space-x-1 items-center">
                    <LuClock className=" icons ml-4" />
                    <span> {timeAgo}</span>
                  </div>
                </div>
                <div className="">
                  <span className="text-xl font-bold text-green-600">
                    {priceData.currency} {priceData?.price?.toLocaleString()}
                  </span>
                  <span className="text-slate-400 text-sm">
                    {priceData.label}
                  </span>
                </div>
              </div>
              {labels.length > 0 && (
                <div className="bg-gray-100 grid grid-cols-2 gap-4 mt-4 p-4 rounded">
                  {labels.map((label, index: number) => (
                    <div key={index} className="">
                      <div className="text-slate-400 text-sm">
                        {label.label}
                      </div>
                      <div className="font-medium text-sm">{label.value}</div>
                    </div>
                  ))}
                </div>
              )}
              <p
                className="text-slate-600 mb-6  break-words overflow-hidden "
                dangerouslySetInnerHTML={{
                  __html: listing.description,
                }}
              ></p>
            </article>
            {!isForPreview && (
              <>
                <div className="rounded-md bg-slate-50 divide-y md:hidden visible shadow ">
                  <div className="py-4 flex-1">
                    <ContactSeller
                      seller={listing.seller}
                      listing={listing}
                      whatsAppText={`https://makazimapya.com${ls}/listings/${slug}/${listing.uuid}`}
                    />
                  </div>
                </div>

                <div className="mt-20">
                  <SimilarItemsByLocation
                    listing={listing}
                    fullLocation={fullLocation}
                    similarItems={similarItemsByLocation}
                  />
                </div>
              </>
            )}
          </div>
          {!isForPreview && (
            <div className="sm:w-full md:w-[400px] md:p-4 px-3 mt-8 md:mt-0 mb-20">
              <div className="sticky space-y-8 top-20">
                <div className="rounded-md bg-slate-50 divide-y  md:flex hidden   shadow ">
                  <div className="py-4 flex-1">
                    <ContactSeller
                      seller={listing.seller}
                      listing={listing}
                      whatsAppText={`https://makazimapya.com${ls}/listings/${slug}/${listing.uuid}`}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ListingDetails;
